/**
 * Client-side placeholder substitution — used by the admin preview when the
 * template is being edited BEFORE any save round-trip (so the backend hasn't
 * yet been asked to render). The public proposal page receives already-rendered
 * blocks from the backend, so this is a no-op there.
 */
const PLACEHOLDER_RE = /\{\{\s*([\w.]+)\s*\}\}/g;

function getPath(root, path) {
  let cur = root;
  for (const part of String(path).split(".")) {
    if (cur == null) return "";
    cur = cur[part];
  }
  return cur == null ? "" : cur;
}

export function renderPlaceholders(value, ctx) {
  if (typeof value !== "string") return value;
  return value.replace(PLACEHOLDER_RE, (_, key) => {
    const v = getPath(ctx, key);
    return v == null ? "" : String(v);
  });
}

export function renderContent(content, ctx) {
  if (content == null) return content;
  if (typeof content === "string") return renderPlaceholders(content, ctx);
  if (Array.isArray(content)) return content.map((c) => renderContent(c, ctx));
  if (typeof content === "object") {
    const out = {};
    for (const [k, v] of Object.entries(content)) out[k] = renderContent(v, ctx);
    return out;
  }
  return content;
}

/** Sample context used only in the admin preview so the editor shows realistic
 * placeholder resolution. */
export const PREVIEW_CONTEXT = {
  contact: {
    name: "Ana Silva",
    name_with_treatment: "Sra. D. Ana Silva",
    treatment: "Sra. D.",
    first_name: "Ana",
    email: "ana.silva@example.pt",
    phone: "+351 912 345 678",
  },
  cared_person: { name: "Manuel Silva", age: "82" },
  cared_person_2: { name: "Rosa Silva", age: "79" },
  proposal: {
    personal_message: "Após conversarmos sobre a situação do seu pai e da sua mãe, preparámos um plano de cuidado individualizado e sereno, à altura de quem tanto se doou por vós.",
    executive_summary: "Propomos apoio domiciliário contínuo de segunda a domingo, com equipa dedicada e supervisão de enfermagem quinzenal. O objetivo é manter Manuel e Rosa em casa, com dignidade e conforto, aliviando a família da carga operacional diária.",
    needs_understanding: "Ambos apresentam mobilidade reduzida e necessitam de apoio nas AVDs. Manuel está em polimedicação (>6 fármacos/dia); Rosa tem incontinência ocasional. A família precisa de tranquilidade para trabalhar, sabendo que estão em boas mãos.",
    commercial_terms: "Serviço contratado por período mínimo de 3 meses. Caução equivalente a 1 mensalidade, restituída ao término do contrato.",
    validity_days: 30,
  },
  gestor: {
    name: "Beatriz Costa",
    title: "Gestora de Cliente Sénior",
    email: "beatriz.costa@caring.pt",
    phone: "800 450 191",
    bio: "Enfermeira especialista com 15 anos de experiência. Coordena diariamente equipas de apoio domiciliário na região de Lisboa.",
  },
  deposit: {
    total_amount: 900,
    kickstart_amount: 500,
    remainder_amount: 400,
    service_start_date: "2026-03-01",
  },
  brand: { name: "Caring", testimonials_url: "https://caring.pt/testemunhos" },
};
